import {useFormContext} from 'react-hook-form';
import {PRESET_PAYROLL_VENDOR_OPTIONS} from '@common/lib/rfcSchema';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@common-web/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {Input} from '@common-web/components/ui/input';

export function PayrollVendorSelect({
  vendorPath,
  payrollSystemPath,
}: {
  vendorPath: string;
  payrollSystemPath: string;
}) {
  const {control, watch, setValue} = useFormContext();
  const selectedVendor = watch(vendorPath);
  const selectedSystem = watch(payrollSystemPath);

  const presetSystemOptions = PRESET_PAYROLL_VENDOR_OPTIONS.find(
    (option) => option.vendor === selectedVendor,
  )?.systems;
  const isPresetVendor = !!presetSystemOptions;
  const isPresetSystem = presetSystemOptions ? presetSystemOptions.includes(selectedSystem) : false;

  return (
    <>
      <FormField
        control={control}
        name={vendorPath}
        render={({field}) => (
          <FormItem>
            <FormLabel>Payroll Vendor (Optional)</FormLabel>
            <div className='space-y-2'>
              <Select
                onValueChange={(value) => {
                  if (value === 'Other') {
                    field.onChange('');
                  } else {
                    field.onChange(value);
                  }

                  // Reset system field when vendor changes
                  let defaultSystem = '';
                  const vendorOption = PRESET_PAYROLL_VENDOR_OPTIONS.find(
                    (option) => option.vendor === value,
                  );
                  if (vendorOption && vendorOption.systems.length > 0) {
                    defaultSystem = vendorOption.systems[0];
                  }
                  setValue(payrollSystemPath, defaultSystem, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }}
                value={isPresetVendor ? field.value : 'Other'}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder='Select vendor' />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {PRESET_PAYROLL_VENDOR_OPTIONS.map((option) => (
                    <SelectItem key={option.vendor} value={option.vendor}>
                      {option.vendor}
                    </SelectItem>
                  ))}
                  <SelectItem value='Other'>Other</SelectItem>
                </SelectContent>
              </Select>
              {!isPresetVendor && (
                <Input
                  placeholder='Enter vendor name'
                  value={field.value || ''}
                  onChange={(e) => field.onChange(e.target.value)}
                />
              )}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name={payrollSystemPath}
        render={({field}) => (
          <FormItem>
            <FormLabel>Payroll System (Optional)</FormLabel>
            {!!presetSystemOptions && presetSystemOptions.length > 0 ? (
              <div className='space-y-2'>
                <Select
                  onValueChange={(value) => {
                    if (value === 'Other') {
                      field.onChange('');
                    } else {
                      field.onChange(value);
                    }
                  }}
                  value={isPresetSystem ? field.value : 'Other'}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder='Select payroll system' />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {presetSystemOptions.map((system) => (
                      <SelectItem key={system} value={system}>
                        {system}
                      </SelectItem>
                    ))}
                    <SelectItem value='Other'>Other</SelectItem>
                  </SelectContent>
                </Select>
                {!isPresetSystem && (
                  <Input
                    placeholder='Enter payroll system'
                    value={field.value || ''}
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                )}
              </div>
            ) : (
              <FormControl>
                <Input {...field} placeholder='Enter payroll system' />
              </FormControl>
            )}
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
}
